/**
 * File-size limits used to safeguard mobile browsers from running out of
 * memory while opening a Quadratic grid.
 *
 * `.grid` files are zstd-compressed, so the in-memory representation after
 * decompression and WASM allocation is typically 5-15x larger than the
 * downloaded byte size. Mobile Safari enforces a ~300-500 MB tab memory
 * ceiling, so we gate on the compressed size of the checkpoint.
 *
 * All values are intentionally conservative. Tune with analytics data once
 * real-world hit rates are collected.
 */

const BYTES_PER_MB = 1024 * 1024;

// Compressed checkpoint size above which we refuse to load on a mobile
// browser (per-user override still available via localStorage).
export const MOBILE_BLOCK_MB = 40;
export const MOBILE_BLOCK_BYTES = MOBILE_BLOCK_MB * BYTES_PER_MB;

// localStorage key that lets a power user bypass the mobile size block on
// this device. Mirrors the pattern used for the non-Blink browser warning.
export const MOBILE_SIZE_BLOCK_OVERRIDE_KEY = 'overrideMobileSizeBlock';

export const isMobileSizeBlockOverridden = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(MOBILE_SIZE_BLOCK_OVERRIDE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const setMobileSizeBlockOverride = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(MOBILE_SIZE_BLOCK_OVERRIDE_KEY, 'true');
    } else {
      window.localStorage.removeItem(MOBILE_SIZE_BLOCK_OVERRIDE_KEY);
    }
  } catch {
    // ignore storage errors (Safari private mode, quota exceeded, etc.)
  }
};

/**
 * Sentinel flag on sessionStorage used by the window-level error handler to
 * signal that the next in-memory load attempt on this device ran out of
 * memory. The next file-load reads and clears this flag to show the mobile
 * fallback screen proactively.
 */
export const MOBILE_OOM_SESSION_FLAG = 'quadratic.mobileOomDetected';

/**
 * Result of a pre-flight size check. `ok` means loading can proceed. `block`
 * means the caller should refuse to load. `unknown` means we could not
 * determine a size (e.g. CORS stripped the header) and the caller should
 * fall back to its normal load behavior.
 */
export type FileSizeCheck =
  | { status: 'ok'; sizeBytes: number }
  | { status: 'block'; sizeBytes: number; limitBytes: number }
  | { status: 'unknown' };

/**
 * Performs a HEAD request against a checkpoint URL to read Content-Length.
 *
 * Returns `unknown` on any failure (network, CORS, missing header) so callers
 * can decide whether to proceed without a size guarantee. The HEAD request
 * itself is bounded by `timeoutMs` to avoid blocking the load path.
 */
export const fetchCheckpointSize = async (
  url: string,
  timeoutMs = 3000
): Promise<{ sizeBytes: number } | { error: unknown }> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    // 'omit' excludes cookies/auth on the cross-origin HEAD; this is a HEAD
    // preflight'd request, but stripping credentials sidesteps CDN/S3 configs
    // that allow anonymous reads but reject Authorization headers. Any CORS
    // failure (preflight blocked, missing ACAO, etc.) propagates as a thrown
    // error and is handled by the catch below.
    const res = await fetch(url, { method: 'HEAD', mode: 'cors', signal: controller.signal, credentials: 'omit' });
    if (!res.ok) {
      return { error: new Error(`HEAD ${url} -> ${res.status}`) };
    }
    const contentLength = res.headers.get('content-length') ?? res.headers.get('Content-Length');
    if (contentLength === null || contentLength === '') {
      return { error: new Error('Content-Length header missing') };
    }
    const sizeBytes = Number(contentLength);
    if (!Number.isFinite(sizeBytes) || sizeBytes < 0) {
      return { error: new Error(`Invalid Content-Length: ${contentLength}`) };
    }
    return { sizeBytes };
  } catch (error) {
    return { error };
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Decides whether a file of the given size is safe to load on the current
 * device. Must be called on the main thread so it can read viewport / UA.
 */
export const evaluateFileSizeForDevice = (sizeBytes: number, isMobile: boolean): FileSizeCheck => {
  if (isMobile && !isMobileSizeBlockOverridden() && sizeBytes > MOBILE_BLOCK_BYTES) {
    return { status: 'block', sizeBytes, limitBytes: MOBILE_BLOCK_BYTES };
  }
  return { status: 'ok', sizeBytes };
};
